/*
 * Copyright (c) 2026 Paxia AI Pty Ltd. All rights reserved.
 * This software is proprietary and confidential.
 * No part of this codebase may be copied, modified, published, distributed, sublicensed, or used without prior written permission from Paxia AI Pty Ltd.
 */

/**
 * SolutionSection — capabilities with expandable detail modals.
 */
import { useState } from "react";
import { motion } from "framer-motion";
import { ShieldCheck, Layers, BrainCircuit, LayoutGrid, Bot, ChevronRight } from "lucide-react";
import PitchSection from "../PitchSection";
import SectionLabel from "../SectionLabel";
import DetailModal from "../DetailModal";
import RiskSettingsImg from "../../../assets/risk settings.jpg";
import MultiBrokerImg from "../../../assets/multi-broker.jpg";
import AiAssistedImg from "../../../assets/ai-assisted.jpg";
import WorkspaceImg from "../../../assets/workspace.jpg";
import NoBotImg from "../../../assets/no-code-bot.jpg";

const iconMap = { ShieldCheck, Layers, BrainCircuit, LayoutGrid, Bot };
const imageMap = {
  "risk-settings": RiskSettingsImg,
  "multi-broker": MultiBrokerImg,
  "ai-assisted": AiAssistedImg,
  "workspace": WorkspaceImg,
  "no-code-bot": NoBotImg,
};

export default function SolutionSection({ data }) {
  const [activeCapability, setActiveCapability] = useState(null);

  return (
    <PitchSection id="solution">
      <SectionLabel>{data.label}</SectionLabel>
      <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4 leading-tight max-w-4xl break-words">
        {data.title}
      </h2>
      <p className="text-gray-400 text-base mb-12">Click any capability for details</p>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 min-w-0">
        {data.capabilities.map((cap, i) => {
          const Icon = iconMap[cap.icon] || LayoutGrid;
          return (
            <motion.button
              key={i}
              onClick={() => setActiveCapability(cap)}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: i * 0.1 }}
              className="group text-left w-full min-w-0 bg-[#0F1826]/60 border border-white/8 rounded-2xl p-5 sm:p-6 hover:border-[#3B82F6]/30 hover:bg-[#3B82F6]/5 transition-all duration-300 cursor-pointer"
            >
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#3B82F6]/15 to-[#4FA9A1]/15 border border-white/10 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                <Icon className="w-6 h-6 text-[#60A5FA]" />
              </div>
              <h3 className="text-white font-semibold mb-2 flex items-start gap-2 min-w-0">
                <span className="min-w-0 break-words">{cap.title}</span>
                <ChevronRight className="w-4 h-4 text-gray-400 group-hover:text-[#60A5FA] group-hover:translate-x-1 transition-all shrink-0 mt-0.5" />
              </h3>
              <p className="text-gray-400 text-sm sm:text-base leading-relaxed break-words">{cap.summary}</p>
            </motion.button>
          );
        })}
      </div>

      {/* Detail modal */}
      <DetailModal
        open={!!activeCapability}
        onClose={() => setActiveCapability(null)}
        title={activeCapability?.title || ""}
      >
        {activeCapability && (
          <div className="space-y-6">
            <div>
              <h3 className="text-base font-semibold text-[#60A5FA] uppercase tracking-wider mb-3">Description</h3>
              <p className="text-gray-300 leading-relaxed">{activeCapability.detail}</p>
            </div>
            <div className="bg-[#4FA9A1]/10 border border-[#4FA9A1]/20 rounded-xl p-5">
              <h3 className="text-base font-semibold text-[#4FA9A1] uppercase tracking-wider mb-2">Key Benefit</h3>
              <p className="text-gray-300 leading-relaxed">{activeCapability.benefit}</p>
            </div>
            {activeCapability.image && (
              <div className="flex justify-center m-4 sm:m-[30px]">
                <img
                  src={imageMap[activeCapability.image]}
                  alt={activeCapability.title}
                  className="rounded-xl border border-white/10 max-w-full sm:max-w-[85%]"
                />
              </div>
            )}
          </div>
        )}
      </DetailModal>
    </PitchSection>
  );
}
